(ns lupapalvelu.ui2.navbar
  (:require [lupapalvelu.ui.common :refer [loc]]
            [lupapalvelu.ui2.button :refer [button]]
            [lupapalvelu.ui2.dropdown :refer [dropdown-menu]]
            [lupapalvelu.ui2.icons :as icons]
            [lupapalvelu.ui2.variant-builder :refer [builder merge-props-classes]]))

(def classes
  (builder
    {:base     {:root            ["twp"
                                  "nobase"
                                  "lp-theme"
                                  "fixed"
                                  "top-[var(--topbar-height)]"
                                  "left-0" "right-0"
                                  "z-[var(--ui2-z-navbar)]"
                                  "flex" "items-center"
                                  "bg-background-container-primary" "text-text-lightbg"
                                  "border-b" "border-[rgba(0,0,0,0.1)]"
                                  "h-[var(--navbar-height)]"]
                :inner           ["mx-auto" "w-full"
                                  "px-4" "sm:px-6" "lg:px-8"
                                  "flex" "items-center" "justify-between"
                                  "h-full"]
                ;; ── Left side ────────────────────────────────────────────
                :left            ["flex" "items-center" "gap-3" "flex-1" "min-w-0"]
                :back-separator  ["w-px" "h-5" "bg-[rgba(0,0,0,0.1)]" "shrink-0"]
                ;; Breadcrumb
                :breadcrumb-list ["flex" "flex-wrap" "items-center"
                                  "gap-1.5" "sm:gap-2.5"
                                  "text-[14px]"
                                  "text-text-muted"
                                  "list-none" "m-0" "p-0"]
                :breadcrumb-btn  ["text-text-muted"
                                  "hover:text-text-lightbg"
                                  "transition-colors"
                                  "p-1"
                                  "rounded-sm"
                                  "no-underline"
                                  "inline-flex" "items-center"
                                  "bg-transparent" "border-0"
                                  "cursor-pointer"
                                  "focus-visible:outline-3"
                                  "focus-visible:outline-input-stroke-focus"]
                :breadcrumb-page ["text-text-lightbg" "font-medium"]
                :breadcrumb-sep  ["inline-flex" "items-center" "self-center"
                                  "text-text-muted"
                                  "leading-none"
                                  "[&>svg]:size-3.5"]
                ;; ── Right side ───────────────────────────────────────────
                :right           ["flex" "items-center" "gap-3"]
                :actions-group   ["flex" "items-center" "gap-3"]
                :action-btn      ["inline-flex" "items-center" "gap-1.5"
                                  "text-sm"
                                  "font-medium"
                                  "rounded-md"
                                  "bg-transparent" "border-0" "shadow-none"
                                  "px-3" "py-2" "h-8"
                                  "text-text-lightbg"
                                  "hover:bg-background-container-secondary" "hover:text-text-hover"
                                  "cursor-pointer"
                                  "relative"]
                :action-badge    ["absolute" "-top-1" "-right-1"
                                  "h-5" "w-5"
                                  "bg-indicator-fill" "text-white"
                                  "rounded-full" "text-xs"
                                  "flex" "items-center" "justify-center"
                                  "pointer-events-none"]}
     :variants {}}))

;; ─────────────────────────────────────────────────────────────────────────────
;; Action button

(defn- link-or-button-props
  [{:keys [href on-click] :as props}]
  (merge (dissoc props :href :on-click)
         (if href
           {:el   :a
            :href href}
           {:type :button})
         (when on-click
           {:on-click on-click})))

(defn- action-btn-content
  "Shared inner content: icon, label text, optional badge pill."
  [{:keys [icon label badge]}]
  [:<>
   (when icon [icon {:size 16 :aria-hidden "true"}])
   [:span label]
   (when (if (number? badge)
           (pos? badge)
           (some? badge))
     [:span (merge-props-classes {} (classes {} :action-badge)) (str badge)])])

(defn- action-button
  "A single action button in the navbar right section.
  :label    – button text
  :icon     – icon component from lupapalvelu.ui2.icons (optional)
  :badge    – number to show as red badge (optional)
  :href     – link href, used when no :items
  :items    – seq of dropdown items (optional, makes button a dropdown trigger)
  :on-click – fn, used when no :items"
  [{:keys [items href on-click label] :as props}]
  (if (seq items)
    [dropdown-menu {:aria-label      label
                    :trigger-content [action-btn-content props]
                    :items           items}]
    [button (link-or-button-props {:href     href
                                   :on-click on-click
                                   :class    (classes {} :action-btn)})
     [action-btn-content props]]))

;; ─────────────────────────────────────────────────────────────────────────────
;; Breadcrumb

(defn- breadcrumb-separator []
  [:li {:role "presentation" :aria-hidden "true" :class (classes {} :breadcrumb-sep)}
   [icons/chevron-right {:size 16}]])

(defn- breadcrumb
  "Renders a breadcrumb trail.
  items – seq of {:label \"...\" :href url :on-click f :icon IconComponent}
  Last item is rendered as the current page (no link/button)."
  [items]
  (let [items    (vec items)
        last-idx (dec (count items))]
    [:nav {:aria-label "breadcrumb"}
     [:ol (merge-props-classes {} (classes {} :breadcrumb-list))
      (map-indexed
        (fn [idx {:keys [label href on-click icon]}]
          ^{:key idx}
          [:<>
           [:li {:class "inline-flex items-center gap-1.5"}
            (if (= idx last-idx)
              [:span {:aria-current "page"
                      :class        (classes {} :breadcrumb-page)}
               (when icon [icon {:size 16 :aria-hidden "true"}])
               label]
              [(if href :a :button)
               (merge {:class     (classes {} :breadcrumb-btn)
                       :tab-index 0}
                      (if href
                        {:href href}
                        {:type "button"})
                      (when on-click
                        {:on-click on-click}))
               (when icon [icon {:size 16 :aria-hidden "true"}])
               (when (and (nil? icon) label) label)
               (when (and icon label) [:span {:class "sr-only"} label])])]
           (when (< idx last-idx)
             [breadcrumb-separator])])
        items)]]))

;; ─────────────────────────────────────────────────────────────────────────────
;; Public component

(defn navbar
  "Props:
    :back-href      – href for back link (preferred for pure navigation)
    :back-on-click  – fn called on back click (optional, shows '< Takaisin' button)
    :back-label     – label for back button (default: (loc \"back\"))
    :breadcrumbs    – seq of {:label :href :on-click :icon}  (optional)
                      Last item is the current page (no button), others are links/buttons.
                      If :icon supplied for an item, label is rendered as sr-only for icon items.
    :actions     – seq of action button maps:
                   {:key      keyword (required)
                    :label    string
                    :icon     icon component from lupapalvelu.ui2.icons (optional)
                    :badge    positive number or string (optional)
                    :href     link href (optional, for simple buttons)
                    :items    seq of dropdown items (optional, makes button a dropdown trigger)
                    :on-click fn (optional, for simple buttons)}
    :secondary-action – secondary CTA button, rendered left of :primary-action {:label :href :on-click} (optional)
    :primary-action – primary action button {:label :href :on-click} (optional)
    :aria-label     – overrides the nav aria-label
    :classes"
  [{:keys [back-href back-on-click back-label breadcrumbs actions secondary-action primary-action aria-label] :as props}]
  [:nav (merge-props-classes {:aria-label (or aria-label (loc "navbar.nav.aria-label"))
                              :class      (:root (:classes props))}
                             (classes {} :root))
   [:div (merge-props-classes {} (classes {} :inner))
    ;; ── Left ─────────────────────────────────────────────────────────────────
    [:div (merge-props-classes {} (classes {} :left))
     (when (or back-href back-on-click)
       [:<>
        [button (link-or-button-props {:href     back-href
                                       :on-click back-on-click
                                       :intent   :text
                                       :size     :xs})
         [icons/chevron-left {:size 16 :aria-hidden "true"}]
         [:span (or back-label (loc "back"))]]
        (when (seq breadcrumbs)
          [:div (merge-props-classes {} (classes {} :back-separator))])])
     (when (seq breadcrumbs)
       [breadcrumb breadcrumbs])]
    ;; ── Right ────────────────────────────────────────────────────────────────
    [:div (merge-props-classes {} (classes {} :right))
     (when (seq actions)
       [:div (merge-props-classes {} (classes {} :actions-group))
        (for [action actions]
          ^{:key (:key action)}
          [action-button action])])
     (when secondary-action
       [button (link-or-button-props {:href     (:href secondary-action)
                                      :on-click (:on-click secondary-action)
                                      :intent   :secondary
                                      :size     :md})
        (:label secondary-action)])
     (when primary-action
       [button (link-or-button-props {:href     (:href primary-action)
                                      :on-click (:on-click primary-action)
                                      :intent   :primary
                                      :size     :md})
        (:label primary-action)])]]])
